import { motion } from "framer-motion";
import { CheckCircle2, HeartPulse } from "lucide-react";
import { useLang } from "@/lib/content";

export default function ExemptionsSection() {
  const { lang } = useLang();

  const groups = [
    {
      rule: "50 / 20",
      cond: {
        es: "Tienes 50 años o más Y llevas 20 años o más como residente permanente.",
        en: "You are 50 or older AND have been a permanent resident for 20+ years.",
      },
      gets: {
        es: ["La prueba de civismo en tu idioma: puedes responder en español.", "Puedes llevar un intérprete oficial a la entrevista.", "El examen de inglés sigue igual (no cambia)."],
        en: ["The civics test in your own language: you may answer in Spanish.", "You may bring an official interpreter to the interview.", "The English test stays the same."],
      },
    },
    {
      rule: "55 / 15",
      cond: {
        es: "Tienes 55 años o más Y llevas 15 años o más como residente permanente.",
        en: "You are 55 or older AND have been a permanent resident for 15+ years.",
      },
      gets: {
        es: ["Estás exento(a) de la prueba de inglés: no leerán ni escribirán contigo.", "La prueba de civismo en tu idioma, con intérprete.", "El civismo es el mismo: preguntas oficiales normales."],
        en: ["You are exempt from the English test: no reading or writing.", "The civics test in your own language, with an interpreter.", "Civics is unchanged: the standard official questions."],
      },
    },
    {
      rule: "65 / 20",
      cond: {
        es: "Tienes 65 años o más Y llevas 20 años o más como residente permanente.",
        en: "You are 65 or older AND have been a permanent resident for 20+ years.",
      },
      gets: {
        es: ["Estudio especial: solo 20 preguntas de las 128 (marcadas en nuestra guía).", "Te preguntan 10 de esas 20 y necesitas 6 correctas.", "La prueba en tu idioma, con intérprete."],
        en: ["Special study: only 20 of the 128 questions (flagged in our guide).", "They ask 10 of those 20 and you need 6 correct.", "The test in your own language, with an interpreter."],
      },
    },
  ];

  return (
    <section id="excepciones" className="bg-paper py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="mx-auto max-w-2xl text-center"
        >
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-heritage">
            {lang === "es" ? "Excepciones oficiales" : "Official exceptions"}
          </p>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-navy sm:text-5xl">
            {lang === "es" ? "¿Puedes hacer el examen " : "Can you take the test "}
            <span className="italic text-heritage">{lang === "es" ? "en español?" : "in Spanish?"}</span>
          </h2>
          <p className="mt-4 text-inkbody">
            {lang === "es"
              ? "Sí — para muchas personas. USCIS tiene tres grupos de excepción por edad y años de residencia. Si cumples una condición, el examen cambia a tu favor:"
              : "Yes — for many people. USCIS has three exception groups based on age and years of residence. If you meet one, the test changes in your favor:"}
          </p>
        </motion.div>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {groups.map((g, i) => (
            <motion.article
              key={g.rule}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              data-testid={`exemption-card-${i}`}
              className="flex flex-col rounded-3xl border border-line bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
            >
              <span className="font-display text-5xl font-semibold text-heritage">{g.rule}</span>
              <p className="mt-4 text-sm font-semibold leading-relaxed text-navy">{lang === "es" ? g.cond.es : g.cond.en}</p>
              <ul className="mt-5 flex-1 space-y-2.5 border-t border-line pt-5">
                {(lang === "es" ? g.gets.es : g.gets.en).map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-[13px] leading-relaxed text-inkbody">
                    <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-emeraldsucc" /> {t}
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          data-testid="exemption-note"
          className="mx-auto mt-10 flex max-w-3xl items-start gap-3 rounded-2xl border border-brass/40 bg-brass/10 p-5 text-sm leading-relaxed text-navy"
        >
          <HeartPulse size={18} className="mt-0.5 shrink-0 text-heritage" />
          {lang === "es"
            ? "Además: si tienes una discapacidad física, de desarrollo o mental, el Formulario N-648 puede exentar del inglés y del civismo con certificación médica. En las 20 preguntas especiales del mazo 65/20 vienen marcadas en nuestra guía descargable."
            : "Also: if you have a physical, developmental, or mental disability, Form N-648 may exempt you from English and civics with medical certification. The 20 special 65/20 questions are flagged in our downloadable guide."}
        </motion.p>
      </div>
    </section>
  );
}
